import { Alert, Snackbar, Stack, TextField, Typography } from "@mui/material"
import { Box } from "@mui/system"
import { Field, Form, Formik, FormikHelpers } from "formik"
import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import * as yup from "yup"
import {
    ACTIVATE_ACCOUNT_CREATE_PASSWORD_DA_ROUTE,
    ACTIVATE_ACCOUNT_CREATE_PASSWORD_ROUTE,
    CREATE_PASSWORD_ROUTE,
} from "../../../constants/routes"
import { usePublicApiMutation } from "../../../core/api/useApiMutation"
import { authenticateByType } from "../loginApi"
import { BackButton } from "../../../shared/components/buttons/BackButton"
import { AuthenticationAction, AuthenticationTokenType, isRequiresCreatePasswordResponse } from "../loginTypes"
import { useAppLocalization } from "../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../localization/components/AppLocalized"
import { CreatePasswordState } from "./CreatePassword"
import { FormikSubmitButton } from "../../../shared/components/forms/Formik"
import { useLocationState } from "../../../core/router/routerHooks"
import { enqueueSnackbar } from "notistack"
import { makeStyles } from "tss-react/mui"
import { StandardLoadingButton } from "../../../shared/components/buttons/LoadingButton"

type FormValues = {
    activationCode: string
}

type SignInWithActivationCodeProps = Readonly<{
    isDefault?: boolean
    isActivateDa?: boolean
}>

const useStyles = makeStyles()((theme) => ({
    formContainer: {
        maxWidth: "25rem",
        width: "100%",
        margin: "auto",
        [theme.breakpoints.down(800)]: {
            flexDirection: "column",
            margin: "0 auto",
        },
    },
    welcomeText: {
        textAlign: "left",
        marginBottom: "0.5rem",
        fontWeight: 400,
        fontSize: "2.5rem",
    },
    welcomeSubText: {
        textAlign: "left",
        marginBottom: "1rem",
        color: theme.palette.text.secondary,
    },
    submitButton: {
        marginTop: "1rem",
        padding: "0.75rem",
        backgroundColor: "#000",
        color: "#fff",
        borderRadius: "3rem",
        "&:hover": {
            backgroundColor: "#333",
        },
    },
    buttonsContainer: {
        marginTop: "1.5rem",
    },
    title: {
        marginBottom: "1rem",
    },
}))

export const SignInWithActivationCode = ({ isDefault = true, isActivateDa = false }: SignInWithActivationCodeProps) => {
    const [showErrorToaster, setShowErrorToaster] = useState(false)
    const [errorMessage, setErrorMessage] = useState("Something went wrong")
    const { classes } = useStyles()

    const [searchParams] = useSearchParams()
    const navigate = useNavigate()

    const { message } = useLocationState<{ message: string | null }>({
        message: null,
    })

    useEffect(() => {
        message &&
            enqueueSnackbar({
                variant: "success",
                message: message,
            })
    }, [message])

    const { l10n } = useAppLocalization()
    const { mutateAsync: callAuthenticateByType } = usePublicApiMutation(authenticateByType, {})

    const activationCode = searchParams.get("otp") ?? ""

    const validationSchema = yup.object({
        activationCode: yup
            .string()
            .min(6, l10n.getString("sign-in-with-activation-code-min-length"))
            .required(l10n.getString("sign-in-with-activation-code-required")),
    })

    const initialValues: FormValues = { activationCode }

    const onSubmit = async ({ activationCode }: FormValues, _helpers: FormikHelpers<FormValues>) => {
        const response = await callAuthenticateByType({
            type: AuthenticationTokenType.ACTIVATION_CODE,
            value: activationCode,
        })

        if (isRequiresCreatePasswordResponse(response)) {
            const navState: CreatePasswordState = {
                user: response.user,
                oneTimeToken: response.authentication.token,
            }
            navigate(
                isDefault
                    ? CREATE_PASSWORD_ROUTE
                    : isActivateDa
                    ? ACTIVATE_ACCOUNT_CREATE_PASSWORD_DA_ROUTE
                    : ACTIVATE_ACCOUNT_CREATE_PASSWORD_ROUTE,
                { state: navState },
            )
        } else {
            setShowErrorToaster(true)
            const action = "authenticationResult" in response ? response.authenticationResult.action : undefined
            setErrorMessage(`Client expected ${AuthenticationAction.CREATE_PASSWORD} but server said: ${action}`)
        }
    }

    return (
        <Box className={classes.formContainer}>
            <Formik<FormValues> initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
                {({ errors, touched, handleSubmit, isSubmitting }) => (
                    <Box component={Form} onSubmit={handleSubmit}>
                        <LocalizedStrict id="sign-in-with-activation-code-title">
                            <Typography
                                component="h1"
                                variant="h5"
                                align="left"
                                className={isDefault ? classes.title : undefined}
                            >
                                Enter your activation code
                            </Typography>
                        </LocalizedStrict>
                        <LocalizedStrict id="sign-in-with-activation-code-field-label" attrs={{ label: true }}>
                            <Field
                                as={TextField}
                                name="activationCode"
                                label="Activation Code"
                                fullWidth
                                margin="normal"
                                variant="outlined"
                                error={touched.activationCode && !!errors.activationCode}
                                helperText={touched.activationCode && errors.activationCode}
                            />
                        </LocalizedStrict>
                        <Stack
                            flexDirection={isDefault ? "column" : "row-reverse"}
                            gap={isDefault ? undefined : "5rem"}
                            marginTop="1rem"
                        >
                            <LocalizedStrict id="sign-in-with-activation-code-submit-button">
                                <StandardLoadingButton
                                    loading={isSubmitting}
                                    fullWidth
                                    type="submit"
                                    variant="contained"
                                    sx={isDefault ? { marginTop: 3, marginBottom: 2 } : { marginBottom: 2 }}
                                >
                                    Submit
                                </StandardLoadingButton>
                            </LocalizedStrict>
                            <BackButton />
                        </Stack>
                    </Box>
                )}
            </Formik>

            <Snackbar
                open={showErrorToaster}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                autoHideDuration={5000}
                onClose={() => setShowErrorToaster(false)}
            >
                <Alert
                    onClose={() => setShowErrorToaster(false)}
                    sx={{ marginBottom: "2rem", marginRight: "1rem" }}
                    severity="error"
                    variant="filled"
                >
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Box>
    )
}
